import { useState } from 'react';
import { useInView } from '../../../hooks/useInView';

type FormData = {
  company: string;
  name: string;
  email: string;
  phone: string;
  inquiry_type: string;
  message: string;
};

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d70ovsmth28qd804cvlg';

export default function ContactSection() {
  const [form, setForm] = useState<FormData>({
    company: '',
    name: '',
    email: '',
    phone: '',
    inquiry_type: 'オンライン商談を希望',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const { ref, inView } = useInView();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'message') {
      if (value.length > 500) return;
      setCharCount(value.length);
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.company || !form.name || !form.email) return;
    setStatus('sending');

    const body = new URLSearchParams();
    Object.entries(form).forEach(([k, v]) => body.append(k, v));

    try {
      const res = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setForm({ company: '', name: '', email: '', phone: '', inquiry_type: 'オンライン商談を希望', message: '' });
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-10 md:py-20 bg-[#1a2e4a]">
      <div ref={ref} className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          {/* Left: CTA Copy */}
          <div className={`transition-all duration-700 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="text-xs tracking-widest text-[#E8601C] font-medium uppercase mb-4 block">
              CONTACT
            </span>
            <h2
              className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight mb-6"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              採用活動の成果を、<br />もっと確実に。
            </h2>
            <p className="text-white/70 text-sm leading-relaxed mb-8">
              まずはサービス内容や料金感についてお気軽にご相談ください。
              資料のご送付、オンライン商談のご調整も承っております。
            </p>

            {/* Points */}
            <ul className="space-y-4">
              {[
                { icon: 'ri-file-list-3-line', text: '資料は2営業日以内にご送付します' },
                { icon: 'ri-calendar-check-line', text: 'オンライン商談は30〜60分で完結' },
                { icon: 'ri-shield-check-line', text: 'しつこい営業連絡は一切しません' },
              ].map((item) => (
                <li key={item.text} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-[#E8601C] text-base`} />
                  </div>
                  <span className="text-white/80 text-sm">{item.text}</span>
                </li>
              ))}
            </ul>

            {/* CTA Variations */}
            <div className="mt-10 p-5 bg-white/5 rounded-xl border border-white/10">
              <p className="text-white/50 text-xs mb-3 uppercase tracking-wide">こんな方にご相談いただいています</p>
              <ul className="space-y-2">
                {[
                  '媒体を契約しているが活用できていない',
                  '採用担当が少なく、工数が足りない',
                  '返信率を改善したい',
                  '母集団形成を安定させたい',
                ].map((t) => (
                  <li key={t} className="flex items-center gap-2">
                    <i className="ri-check-line text-[#E8601C] text-xs" />
                    <span className="text-white/70 text-xs">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right: Form */}
          <div
            className={`bg-white rounded-2xl p-6 sm:p-8 transition-all duration-700 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: inView ? '200ms' : '0ms' }}
          >
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center h-full py-10 text-center gap-4">
                <div className="w-14 h-14 flex items-center justify-center bg-green-50 rounded-full">
                  <i className="ri-check-line text-green-500 text-2xl" />
                </div>
                <h3 className="text-lg font-bold text-[#1a2e4a]" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  送信が完了しました
                </h3>
                <p className="text-sm text-[#666] leading-relaxed">
                  お問い合わせありがとうございます。<br />
                  2営業日以内にご連絡いたします。
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-2 text-sm text-[#1a2e4a] underline cursor-pointer"
                >
                  別のお問い合わせをする
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <h3 className="text-base font-bold text-[#1a2e4a] mb-1" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  お問い合わせ
                </h3>

                {/* Inquiry Type */}
                <div>
                  <label className="text-xs font-medium text-[#444] block mb-1.5">
                    お問い合わせ種別 <span className="text-[#E8601C]">*</span>
                  </label>
                  <select
                    name="inquiry_type"
                    value={form.inquiry_type}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#333] focus:outline-none focus:border-[#1a2e4a] bg-white cursor-pointer"
                  >
                    <option>オンライン商談を希望</option>
                    <option>サービス内容の確認</option>
                    <option>料金について</option>
                    <option>その他</option>
                  </select>
                </div>

                {/* Company */}
                <div>
                  <label className="text-xs font-medium text-[#444] block mb-1.5">
                    会社名 <span className="text-[#E8601C]">*</span>
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    placeholder="株式会社〇〇"
                    required
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#333] placeholder-gray-300 focus:outline-none focus:border-[#1a2e4a]"
                  />
                </div>

                {/* Name */}
                <div>
                  <label className="text-xs font-medium text-[#444] block mb-1.5">
                    お名前 <span className="text-[#E8601C]">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="山田 太郎"
                    required
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#333] placeholder-gray-300 focus:outline-none focus:border-[#1a2e4a]"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="text-xs font-medium text-[#444] block mb-1.5">
                    メールアドレス <span className="text-[#E8601C]">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="example@company.com"
                    required
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#333] placeholder-gray-300 focus:outline-none focus:border-[#1a2e4a]"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="text-xs font-medium text-[#444] block mb-1.5">
                    電話番号（任意）
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="03-0000-0000"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#333] placeholder-gray-300 focus:outline-none focus:border-[#1a2e4a]"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="text-xs font-medium text-[#444] block mb-1.5">
                    ご相談内容・ご質問（任意）
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="現状の課題や気になっていることをお気軽にご記入ください"
                    rows={3}
                    maxLength={500}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#333] placeholder-gray-300 focus:outline-none focus:border-[#1a2e4a] resize-none"
                  />
                  <p className="text-right text-xs text-[#bbb] mt-1">{charCount} / 500</p>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full bg-[#1a2e4a] text-white text-sm font-medium py-3 rounded-lg hover:bg-[#263f66] transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {status === 'sending' ? '送信中...' : '送信する'}
                </button>
                {status === 'error' && (
                  <p className="text-xs text-red-500 text-center">
                    送信に失敗しました。時間をおいて再度お試しください。
                  </p>
                )}
                <p className="text-xs text-[#bbb] text-center leading-relaxed">
                  ご入力いただいた情報は、サービスのご案内にのみ使用いたします。
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
